import { DOCUMENT } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { FeedbackRoundService } from '@brafton/feedback-api';
import { firstValueFrom } from 'rxjs';
import { BRAFTON_SESSION_COOKIE } from '../../core/brafton-session-cookie';

/**
 * Message posted up to the embedding parent. The `source` discriminator lets the
 * parent ignore unrelated `postMessage`s; `token` carries the raw Auth0 ID-token
 * JWT on success, or null on failure.
 */
export interface AuthBridgeMessage {
  source: 'feedback-auth-bridge';
  auth: 'success' | 'failed';
  token: string | null;
}

/**
 * Message the parent sends to start the flow. It carries the project-scoped
 * `validateround` JWT and the round id; the parent's domain is taken from the
 * browser-set `event.origin`, never from the message body (which is spoofable).
 */
interface AuthBridgeRequest {
  type: typeof REQUEST_TYPE;
  jwt: string;
  feedbackRoundId: string;
}

const REQUEST_TYPE = 'feedback-auth-bridge:request';

/** The verified requester to reply to, captured when a gesture is needed. */
interface PendingRequester {
  origin: string;
  source: Window;
}

/**
 * TEMPORARY auth bridge, embedded as an iframe by a trusted customer site.
 *
 * Flow (driven by the parent's request message, never on load — we need the
 * round context first):
 *  1. Parent posts `{ type, jwt, feedbackRoundId }`.
 *  2. We take the parent's origin from the browser-set `event.origin`
 *     (UNSPOOFABLE) — not from the message — and call `embed-allowed` with the
 *     parent's JWT to confirm that origin is registered for the round.
 *  3. Only if allowed do we detect the Brafton Auth0 session and release the
 *     ID-token JWT, posted back ONLY to that exact `event.origin`.
 *
 * Brafton-session detection (after authorization) uses the Storage Access API:
 * read the `.brafton.com` cookie directly (same-origin/local or already-granted
 * → frictionless), else one click grants storage access (or runs an Auth0
 * popup). Public/anonymous visitors yield `{ auth: 'failed', token: null }`.
 */
@Component({
  selector: 'app-auth-bridge-page',
  templateUrl: './auth-bridge-page.html',
  styleUrl: './auth-bridge-page.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AuthBridgePage implements OnInit, OnDestroy {
  private readonly document = inject(DOCUMENT);
  private readonly feedbackRounds = inject(FeedbackRoundService);

  /** Shown only when the browser needs a user gesture to read the session. */
  protected readonly needsGesture = signal(false);
  /** True while the gesture-driven flow is running, to disable the control. */
  protected readonly busy = signal(false);

  /** Requester awaiting a gesture-driven result (set when needsGesture is true). */
  private pendingRequester: PendingRequester | null = null;

  ngOnInit(): void {
    window.addEventListener('message', this.handleParentRequest);
  }

  ngOnDestroy(): void {
    window.removeEventListener('message', this.handleParentRequest);
  }

  /**
   * Handles a parent request: authorize the embedding origin against the round,
   * then resolve and post the Brafton session state back to that origin.
   */
  private readonly handleParentRequest = async (event: MessageEvent): Promise<void> => {
    const data = event.data as Partial<AuthBridgeRequest> | null;
    if (!data || data.type !== REQUEST_TYPE) {
      return;
    }

    const origin = event.origin; // browser-set; cannot be spoofed by the parent
    const source = event.source as Window | null;
    if (!source) {
      return;
    }

    const jwt = typeof data.jwt === 'string' ? data.jwt : '';
    const feedbackRoundId = typeof data.feedbackRoundId === 'string' ? data.feedbackRoundId : '';
    if (!jwt || !feedbackRoundId) {
      this.postTo(source, origin, this.failed());
      return;
    }

    // Gate: is THIS origin registered to embed THIS round? (server-side DB check)
    const allowed = await this.isEmbedAllowed(feedbackRoundId, origin, jwt);
    if (!allowed) {
      this.postTo(source, origin, this.failed());
      return;
    }

    // Authorized — now detect the Brafton session without prompting if possible.
    const directToken = this.readSessionCookie();
    if (directToken) {
      this.postTo(source, origin, this.success(directToken));
      return;
    }
    if (this.storageAccessSupported() && (await this.hasStorageAccessSafe())) {
      // Access already granted, so a missing cookie means no Brafton session.
      this.postTo(source, origin, this.failed());
      return;
    }

    // Need a user gesture to grant storage access (or run a popup login).
    this.pendingRequester = { origin, source };
    this.needsGesture.set(true);
  };

  /** Runs inside a real click, where the gesture-gated APIs are allowed. */
  protected async continueAsBrafton(): Promise<void> {
    const requester = this.pendingRequester;
    if (!requester) {
      return;
    }
    this.busy.set(true);
    try {
      if (this.storageAccessSupported()) {
        try {
          await (this.document as Document).requestStorageAccess();
          const grantedToken = this.readSessionCookie();
          if (grantedToken) {
            this.finish(requester, this.success(grantedToken));
            return;
          }
        } catch {
          // Access denied — fall through.
        }
      }
      // Granted-but-no-cookie, or SAA unsupported/denied. There is no interactive
      // fallback: the org auth library signs in by full-page redirect to Auth0,
      // which a cross-site iframe cannot perform (and framing Auth0 is blocked).
      // So report no session; the visitor signs in on the app itself, which sets
      // the .brafton.com cookie this bridge reads. See the follow-up note in
      // core/skynet-auth.config.ts.
      this.finish(requester, this.failed());
    } catch {
      this.finish(requester, this.failed());
    } finally {
      this.busy.set(false);
    }
  }

  /** Posts the result, hides the prompt, and clears the pending requester. */
  private finish(requester: PendingRequester, message: AuthBridgeMessage): void {
    this.needsGesture.set(false);
    this.pendingRequester = null;
    this.postTo(requester.source, requester.origin, message);
  }

  /**
   * Asks the API whether `origin` is registered to embed `feedbackRoundId`,
   * authenticated with the parent's validateround JWT (passed explicitly so the
   * auth interceptor sends it verbatim rather than the stored dev token).
   */
  private async isEmbedAllowed(feedbackRoundId: string, origin: string, jwt: string): Promise<boolean> {
    try {
      return await firstValueFrom(
        this.feedbackRounds.checkEmbedAllowed(feedbackRoundId, origin, jwt),
      );
    } catch {
      return false;
    }
  }

  private success(token: string): AuthBridgeMessage {
    return { source: 'feedback-auth-bridge', auth: 'success', token };
  }

  private failed(): AuthBridgeMessage {
    return { source: 'feedback-auth-bridge', auth: 'failed', token: null };
  }

  /** Posts a message to one specific window at one specific origin (never '*'). */
  private postTo(source: Window, origin: string, message: AuthBridgeMessage): void {
    source.postMessage(message, origin);
  }

  /** Reads the raw ID-token JWT from the `.brafton.com` cookie, if present. */
  private readSessionCookie(): string | null {
    const entries = (this.document.cookie ?? '').split('; ');
    for (const entry of entries) {
      const separator = entry.indexOf('=');
      if (separator > -1 && entry.slice(0, separator) === BRAFTON_SESSION_COOKIE) {
        const value = entry.slice(separator + 1);
        return value || null;
      }
    }
    return null;
  }

  private storageAccessSupported(): boolean {
    return typeof (this.document as Document).requestStorageAccess === 'function';
  }

  private async hasStorageAccessSafe(): Promise<boolean> {
    try {
      return await (this.document as Document).hasStorageAccess();
    } catch {
      return false;
    }
  }
}
